
import Vue from 'vue';
import { oneOf } from '@core/utils/helpers';
import BaseButton from '@core/components/UI/BaseButton.vue';
import Dropdown from '@core/components/UI/dropdown/Dropdown.vue';
import MenuLightbox from '@root/common/components/base/MenuLightbox.vue';
import { stripTags } from '@core/filters/htmlFormater';
import { faArrowRotateLeft, faCheck } from '@delfi.lt/fa-kit/icons/modules/classic/solid';

interface Option {
  name: string;
  value: unknown;
}

interface Data {
  selected: string[] | string;
  closeDropdown: { value: boolean; timestamp: string } | null;
  showLightbox: boolean;
}

interface Props {
  value: unknown;
  options: Option[];
  placeholder: string;
  size: string;
  lightbox: boolean;
  showReset: boolean;
  resetText: string;
}

interface Computed {
  classes: (string | { [x: string]: boolean })[];
}

interface Methods {
  handleSelect: () => void;
  handleReset: () => void;
  select: (name: string, value: unknown) => void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    BaseButton,
    Dropdown,
    MenuLightbox,
  },
  filters: {
    stripTags,
  },
  props: {
    /**
     * Selected value. Chooses value from options
     */
    value: {
      type: undefined,
      default: null,
      required: true,
    },
    /**
     * @values [{ name: 'name', value: 'value' }]
     */
    options: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    /**
     * @values 'auto', 'small', 'default'
     */
    size: {
      validator(value: string) {
        return oneOf(value, ['auto', 'small', 'default']);
      },
      type: String,
      default: () => 'default',
      required: false,
    },
    lightbox: {
      type: Boolean,
      default: false,
      required: false,
    },
    showReset: {
      type: Boolean,
      default: false,
      required: false,
    },
    resetText: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      selected: [],
      closeDropdown: null,
      showLightbox: false,
      faArrowRotateLeft,
      faCheck,
    };
  },
  computed: {
    classes() {
      return [
        'form-select',
        'form-select--multiple',
        {
          'form-select--lightbox': this.lightbox,
          [`form-select--size-${this.size}`]: this.size !== 'auto',
        },
      ];
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(value) {
        if (value) {
          this.selected = value;
        }
      },
    },
  },
  methods: {
    handleSelect() {
      this.$emit('input', this.selected);
      this.showLightbox = !this.showLightbox;
    },
    handleReset() {
      this.selected = [];
      this.handleSelect();
      this.closeDropdown = {
        value: true,
        timestamp: String(new Date().getTime()),
      };
    },
    select(value) {
      this.closeDropdown = {
        value: true,
        timestamp: String(new Date().getTime()),
      };

      // Try to construct array if selection has one entry
      if (typeof this.selected === 'string') {
        this.selected = this.selected.split(',');
      }

      if (this.selected.includes(value)) {
        this.selected = this.selected.filter((item) => item !== value);
      } else {
        this.selected.push(value);
      }

      if (!this.lightbox) {
        this.$emit('input', this.selected);
      }
    },
  },
});
